import { useEffect, useRef } from 'react'
import { ClientJS } from 'clientjs'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'reactstrap'
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid'
import { recaptchaKey } from 'configs'
import useRecaptchaV3 from 'hooks/useRecaptchaV3'
import { getMetaContent } from 'helpers'
import { login } from 'features/public-views/login/loginSlice'
import {
  useCheckDeviceWaMutation,
  useCreateDeviceWaMutation,
  useLoginWaMutation,
} from 'features/public-views/login-new/loginNewApiSlice'
import { P } from 'components'

const LoginWa = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const recaptcha = useRecaptchaV3(recaptchaKey)
  const isProcessingRef = useRef(false)

  const [checkDeviceWa] = useCheckDeviceWaMutation()
  const [createDeviceWa] = useCreateDeviceWaMutation()
  const [loginWa] = useLoginWaMutation()

  const searchParams = new URLSearchParams(window.location.search)
  const tokenLogin = searchParams.get('code')

  const getClientData = (client) => {
    const methods = Object.getOwnPropertyNames(
      Object.getPrototypeOf(client),
    ).filter(
      (method) =>
        typeof client[method] === 'function' && method !== 'constructor',
    )

    const keysToRemove = new Set([
      'getCPU',
      'getBrowserVersion',
      'getUserAgentLowerCase',
      'isMobileIOS',
      'isIphone',
      'getDeviceType',
      'getUserAgent',
      'isChrome',
      'isFirefox',
      'isIphone',
      'isIpod',
      'isMac',
      'isMobile',
      'isMobileBlackBerry',
      'isMobileMajor',
      'isMobileOpera',
      'isMobileSafari',
      'isMobileWindows',
      'isOpera',
      'isSafari',
      'isUbuntu',
      'isWindows',
      'isIpad',
      'isJava',
      'isLinux',
      'isSolaris',
      'isIE',
      'isMobileAndroid',
      'isFont',
    ])

    return methods.reduce((acc, method) => {
      if (!keysToRemove.has(method)) {
        try {
          acc[method] = client[method]()
        } catch (error) {
          acc[method] = `Error: ${error.message}`
        }
      }
      return acc
    }, {})
  }

  const generateHash = (data) => {
    const combinedFingerprint = `${data.fingerprint}${data.customFingerprint}`
    return uuidv5(combinedFingerprint, uuidv4()).replace(/-/g, '')
  }

  const getStoredBrowserData = () => ({
    browserHash: localStorage.getItem('browserHash') || null,
    browserFingerPrint: localStorage.getItem('browserFingerPrint') || null,
    browserCustomFingerPrint:
      localStorage.getItem('browserCustomFingerPrint') || null,
  })

  const setNewBrowserData = (data) => {
    const newHash = uuidv4().replace(/-/g, '')
    localStorage.setItem('browserHash', newHash)
    localStorage.setItem('browserFingerPrint', data?.getFingerprint)
    localStorage.setItem('browserCustomFingerPrint', data?.getCustomFingerprint)
    return {
      browserHash: newHash,
      browserFingerPrint: data?.getFingerprint,
      browserCustomFingerPrint: data?.getCustomFingerprint,
    }
  }

  const isMatchingDevice = (serverData, localData) => {
    return (
      serverData?.browserCustomFingerPrint ===
      localData?.browserCustomFingerPrint &&
      serverData?.browserFingerPrint === localData?.browserFingerPrint &&
      serverData?.browserHash === localData?.browserHash
    )
  }

  useEffect(() => {
    const handleWaLogin = async () => {
      if (!tokenLogin) {
        enqueueSnackbar('Token tidak ditemukan', { variant: 'error' })
        navigate('/login')
        return
      }

      if (isProcessingRef.current) return
      isProcessingRef.current = true

      const getRecaptchaToken = async (action, retries = 15) => {
        for (let i = 0; i < retries; i++) {
          if (window.grecaptcha && window.grecaptcha.execute) {
            try {
              const token = await window.grecaptcha.execute(recaptchaKey, { action })
              if (token) return token
            } catch (e) {
              console.error('Error executing reCAPTCHA:', e)
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
        return null
      }

      const recaptchaToken = await getRecaptchaToken('login_wa')
      if (!recaptchaToken) {
        isProcessingRef.current = false
        enqueueSnackbar('Gagal memuat sistem keamanan (reCAPTCHA)', { variant: 'error' })
        return
      }

      try {
        const client = new ClientJS()
        const clientData = getClientData(client)

        const dataForHash = {
          fingerprint: clientData.getFingerprint,
          customFingerprint: clientData.getCustomFingerprint,
        }

        clientData.hash = generateHash(dataForHash)

        let storedData = getStoredBrowserData()
        if (
          !storedData.browserHash ||
          !storedData.browserFingerPrint ||
          !storedData.browserCustomFingerPrint
        ) {
          storedData = setNewBrowserData(clientData)
        }

        const checkPayload = {
          tokenLogin,
          ...storedData,
          'g-recaptcha-response': recaptchaToken,
        }

        let deviceMatched = false
        const resCheck = await checkDeviceWa(checkPayload)

        if (resCheck?.data && isMatchingDevice(resCheck?.data?.data, storedData)) {
          deviceMatched = true
        }

        if (!deviceMatched) {
          const freshRecaptchaForCreate = await getRecaptchaToken('login_wa')
          const createPayload = {
            tokenLogin,
            ...storedData,
            'g-recaptcha-response': freshRecaptchaForCreate,
            deviceToken: clientData.hash,
            deviceInfo: { browserInfo: clientData },
          }
          await createDeviceWa(createPayload)
        }

        const freshRecaptchaForLogin = await getRecaptchaToken('login_wa')
        const loginPayload = {
          tokenLogin,
          ...storedData,
          'g-recaptcha-response': freshRecaptchaForLogin,
          deviceToken: clientData.hash,
        }

        const resLogin = await loginWa(loginPayload)
        if (!resLogin?.error) {
          dispatch(
            login({
              identity: 'WhatsApp User',
              ...resLogin?.data,
            })
          )
          window.location.replace('/')
        } else {
          throw resLogin
        }
      } catch (error) {
        console.error(error)
        enqueueSnackbar(
          error?.error?.data?.meta?.message ??
          error?.data?.meta?.message ??
          error?.data?.message ??
          'Gagal login via WhatsApp.',
          {
            variant: 'error',
          }
        )
        navigate('/login')
      }
    }

    handleWaLogin()
  }, [tokenLogin, enqueueSnackbar, navigate, dispatch, checkDeviceWa, createDeviceWa, loginWa, recaptcha])

  return (
    <div
      className="d-flex justify-content-center align-items-center vh-100 w-100"
      style={{ backgroundColor: '#ffffff' }}
    >
      <style>
        {`
          @keyframes pulse-logo {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animated-logo {
            animation: pulse-logo 2s infinite ease-in-out;
          }
          .loading-bar-container {
            width: 120px;
            height: 4px;
            background-color: #f2f4f7;
            border-radius: 4px;
            overflow: hidden;
            margin: 24px auto;
          }
          .loading-bar {
            height: 100%;
            width: 40%;
            background-color: #006386;
            border-radius: 4px;
            animation: loading-slide 1.5s infinite ease-in-out;
          }
          @keyframes loading-slide {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(250%); }
          }
        `}
      </style>
      <div
        className="text-center d-flex flex-column align-items-center justify-content-center w-100 h-100"
        style={{
          maxWidth: '450px',
          padding: '40px 20px',
          backgroundColor: '#ffffff',
        }}
      >
        <img
          src={getMetaContent('og:image')}
          alt={`${getMetaContent('og:title')} logo`}
          className="animated-logo"
          style={{
            width: '100%',
            maxWidth: '220px',
            height: 'auto',
          }}
        />

        <div className="loading-bar-container">
          <div className="loading-bar"></div>
        </div>

        <P size="large" bold className="mb-2" style={{ color: '#101828' }}>
          Sedang Memproses...
        </P>
        <P size="regular" style={{ color: '#667085', lineHeight: '1.5' }}>
          Mohon tunggu sebentar, kami sedang menyiapkan halaman Anda.
        </P>
      </div>
    </div>
  )
}

export default LoginWa
